/**
 * Logger utility for consistent logging across the application
 * Inspired by winston logger pattern but adapted for browser environment
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LogConfig {
  level: LogLevel
  isProd: boolean
  enableTimestamp: boolean
}

// Determine if running in production mode
const isProd = import.meta.env.PROD

// Default log level from environment or 'info'
const defaultLevel = (import.meta.env.VITE_LOG_LEVEL as LogLevel) || 'info'

const config: LogConfig = {
  level: defaultLevel,
  isProd,
  enableTimestamp: !isProd,
}

// Log level priorities
const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
}

// ANSI color codes for dev environment (browser console supports colors)
const COLORS: Record<LogLevel, string> = {
  debug: '#9E9E9E', // Gray
  info: '#2196F3', // Blue
  warn: '#FF9800', // Orange
  error: '#F44336', // Red
}

/**
 * Format timestamp
 */
const formatTimestamp = (): string => {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const year = now.getFullYear()
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')

  return `${month}-${day}-${year} ${hours}:${minutes}:${seconds}`
}

/**
 * Format log arguments (similar to winston's splat)
 */
const formatArgs = (args: any[]): string => {
  return args
    .map((arg) => {
      if (typeof arg === 'object') {
        try {
          return JSON.stringify(arg, null, isProd ? 0 : 2)
        } catch (error) {
          return String(arg)
        }
      }
      return String(arg)
    })
    .join(' ')
}

/**
 * Check if log level should be logged
 */
const shouldLog = (level: LogLevel): boolean => {
  return LOG_LEVELS[level] >= LOG_LEVELS[config.level]
}

/**
 * Core logging function
 */
const log = (level: LogLevel, message: string, ...args: any[]): void => {
  if (!shouldLog(level)) {
    return
  }

  const formattedArgs = args.length > 0 ? ' ' + formatArgs(args) : ''
  const timestamp = config.enableTimestamp ? formatTimestamp() : ''

  if (isProd) {
    // Production format: level(pid) message args
    // Note: In browser, we use a unique identifier instead of process.pid
    const sessionId = getSessionId()
    const logMessage = `${level}(${sessionId}) ${message}${formattedArgs}`

    // Use native console methods in production
    switch (level) {
      case 'debug':
      case 'info':
        console.log(logMessage)
        break
      case 'warn':
        console.warn(logMessage)
        break
      case 'error':
        console.error(logMessage)
        break
    }
  } else {
    // Development format: colored, with timestamp
    const color = COLORS[level]
    const logMessage = `${level}(${getSessionId()}) ${timestamp} ${message}${formattedArgs}`

    // Use console methods with color styling
    switch (level) {
      case 'debug':
      case 'info':
        console.log(`%c${logMessage}`, `color: ${color}`)
        break
      case 'warn':
        console.warn(`%c${logMessage}`, `color: ${color}`)
        break
      case 'error':
        console.error(`%c${logMessage}`, `color: ${color}`)
        break
    }
  }
}

/**
 * Get or create a session ID (alternative to process.pid in browser)
 */
let sessionId: string | null = null

const getSessionId = (): string => {
  if (!sessionId) {
    // Generate a short random ID for this session
    sessionId = Math.random().toString(36).substring(2, 8)
  }
  return sessionId
}

/**
 * Logger instance with convenient methods
 */
const logger = {
  /**
   * Log debug message
   */
  debug: (message: string, ...args: any[]): void => {
    log('debug', message, ...args)
  },

  /**
   * Log info message
   */
  info: (message: string, ...args: any[]): void => {
    log('info', message, ...args)
  },

  /**
   * Log warning message
   */
  warn: (message: string, ...args: any[]): void => {
    log('warn', message, ...args)
  },

  /**
   * Log error message
   */
  error: (message: string, ...args: any[]): void => {
    log('error', message, ...args)
  },

  /**
   * Set log level dynamically
   */
  setLevel: (level: LogLevel): void => {
    config.level = level
  },

  /**
   * Get current log level
   */
  getLevel: (): LogLevel => {
    return config.level
  },
}

export default logger
